import { Alert, Box, Button, Typography, useTheme } from '@mui/material';
import Breadcrumbs from 'components/breadcrumbs';
import Dialog from 'components/dialog';
import Divider from 'components/divider';
import List, { HeadCell } from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow, { BodyTableRow } from 'components/list/list-row';

import FilterSidebar from 'components/filter-sidebar';
import Paper from 'components/paper';
import Tooltip from 'components/tooltip';
import { useAccountState } from 'contexts/account/account-context';
import dayjs from 'dayjs';
import { useGet } from 'hooks/use-get';
import { AlertTriangle } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber, FormattedTime, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import _ from 'underscore';
import { SiteAutocomplete } from 'views/order-feed/components/site-autocomplete';
import OrderFeedTabsHeader from 'views/order-feed/components/order-feed-tabs-header';
import { OrderFeedProvider } from 'views/order-feed/context/order-feed-context';
import { useSitesState } from 'views/order-feed/context/sites-context';
import { getOrderDistributions } from 'views/order-feed/features/order-distribution/api/get-distributions';
import CreateOrderDistribution from 'views/order-feed/features/order-distribution/components/create-order-distribution';
import { OrderDistribution } from 'views/order-feed/features/order-distribution/types/order-distribution.type';
import { OrderFeedParams } from 'views/order-feed/types/order-feed-params.type';
import { OrderDistributionStatus } from 'views/order-feed/features/order-distribution/types/order-distribution-status.enum';

const TIME_TO_EDIT = 180;
const PAGE_NAME_ID = 'orderFeed.orderDistribution';

const ListOrderDistributions: React.FC = () => {
  const { account } = useAccountState();
  const params = useParams<OrderFeedParams>();
  const intl = useIntl();
  const theme = useTheme();
  const {
    sitesQuery: { data: sites },
  } = useSitesState();

  //#region STATES
  const [undistributed, setUndistributed] = useState<OrderDistribution[]>([]);
  const [distributed, setDistributed] = useState<OrderDistribution[]>([]);
  const selectedSite = sites?.find((site) => site.globalSiteId === params.globalSiteId) || null;
  const createDistributionRef = useRef<React.ElementRef<typeof Dialog>[]>([]);
  const editDistributionRef = useRef<React.ElementRef<typeof Dialog>[]>([]);
  const selectedSiteId = params.siteErpId && params.globalSiteId ? `${params.siteErpId}/${params.globalSiteId}` : '';

  const options: HeadCell[] = [
    {
      id: 'siteName',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.siteName` }),
    },
    {
      id: 'requestedDeliveryDate',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.dispatchDate` }),
    },
    {
      id: 'vesselName',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.vesselName` }),
    },
    {
      id: 'eta',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.deliveryDate` }),
    },
    {
      id: 'orderQuantity',
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.orderQuantity` }),
      align: 'right',
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
    },
  ];

  const bodyOptions: HeadCell[] = [
    {
      id: 'orderNumber',
      autoWidth: true,
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.body.orderNumber` }),
    },
    {
      id: 'productName',
      autoWidth: true,
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.body.productName` }),
    },
    {
      id: 'siloName',
      autoWidth: true,
      label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.body.siloName` }),
    },
  ];

  //#endregion STATES

  //#region FETCH

  const { data: allDistributions, isLoading } = useGet(
    ['getOrderDistributions', account.company!.customerErpId],
    ({ queryKey }) => getOrderDistributions(String(queryKey[1])),
    {
      enabled: !!account.company!.customerErpId,
    }
  );

  useEffect(() => {
    const [undistributedDistributions, distributedDistributions] = _.chain(allDistributions ?? [])
      .filter((distribution) => (params.globalSiteId ? distribution.siteId === params.globalSiteId : true))
      .sortBy((d) => d.eta)
      .sortBy((d) => d.siteName)
      .map((distribution) => ({
        ...distribution,
        orderQuantity: distribution.products.reduce((total, product) => total + product.quantity, 0),
      }))
      .partition((d) => d.status === OrderDistributionStatus.NOT_STARTED)
      .value();

    setUndistributed(undistributedDistributions);
    setDistributed(distributedDistributions);
  }, [allDistributions, params.globalSiteId]);

  //#endregion FETCH

  //#region HANDLERS

  const shouldDisableEdit = (eta: string) => {
    return dayjs(eta).utc().diff(dayjs().utc(), 'minute') <= TIME_TO_EDIT;
  };

  //#endregion HANDLERS

  return (
    <>
      <OrderFeedTabsHeader
        utils={
          <Breadcrumbs
            crumbs={
              !!selectedSiteId
                ? [
                    {
                      text:
                        account.company?.addressAccess.find((s) => s.siteErpId === params.siteErpId)?.name || selectedSiteId,
                      url: `${params.siteErpId}/${params.globalSiteId}`,
                    },
                  ]
                : undefined
            }
          />
        }
      />
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <FilterSidebar>
          <SiteAutocomplete value={selectedSite} route={{ basePath: '/order-feed/order-distribution' }} />
        </FilterSidebar>
        <Alert severity="info">
          <FormattedMessage id={`${PAGE_NAME_ID}.infoText`} />
        </Alert>

        <Paper transparent>
          <Box>
            <Box>
              <Typography>
                <FormattedMessage id={`${PAGE_NAME_ID}.readyForDistribution`} />
              </Typography>
              <Divider sx={{ my: 4 }} />
            </Box>
            <Box>
              <List
                options={options}
                rows={undistributed}
                isLoading={isLoading}
                sort={setUndistributed}
                noResult={{
                  show: !undistributed.length,
                  text: intl.formatMessage({ id: `general.noResults` }),
                }}>
                {undistributed?.map((distribution, idx) => {
                  const isEditDisabled = shouldDisableEdit(distribution.eta);

                  return (
                    <ListRow
                      key={idx}
                      body={
                        !!distribution.distributions.length
                          ? {
                              options: [
                                ...bodyOptions,
                                {
                                  id: 'quantity',
                                  autoWidth: true,
                                  label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.body.distributedQuantity` }),
                                  align: 'right',
                                },
                              ],
                              span: 7,
                              children: distribution.distributions
                                .sort((a, b) => a.siloName.toLowerCase().localeCompare(b.siloName.toLowerCase()))
                                .map((line, idxx) => (
                                  <BodyTableRow key={`${idx}_body_${idxx}`}>
                                    <ListCell>{line.orderNumber}</ListCell>
                                    <ListCell>{line.itemName}</ListCell>
                                    <ListCell>{line.siloName}</ListCell>
                                    <ListCell align="right">
                                      <FormattedNumber value={line.quantity} style={`unit`} unit={'kilogram'} />
                                    </ListCell>
                                  </BodyTableRow>
                                )),
                            }
                          : {
                              options: [
                                ...bodyOptions,
                                {
                                  id: 'quantity',
                                  autoWidth: true,
                                  label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.body.quantity` }),
                                  align: 'right',
                                },
                              ],
                              span: 7,
                              children: distribution.products.map((line, idxx) => (
                                <BodyTableRow key={`${idx}_body_${idxx}`}>
                                  <ListCell>{line.orderNumber}</ListCell>
                                  <ListCell>{line.itemName}</ListCell>
                                  <ListCell>{intl.formatMessage({ id: 'general.empty' })}</ListCell>
                                  <ListCell align="right">
                                    <FormattedNumber value={line.quantity} style={`unit`} unit={'kilogram'} />
                                  </ListCell>
                                </BodyTableRow>
                              )),
                            }
                      }>
                      <ListCell>{distribution.siteName}</ListCell>
                      <ListCell>
                        <FormattedDate value={distribution.requestedDeliveryDate} />
                      </ListCell>
                      <ListCell>{distribution.vesselName}</ListCell>
                      <ListCell>
                        <FormattedDate value={distribution.eta} />
                        &nbsp;
                        <FormattedTime value={distribution.eta} />
                      </ListCell>
                      <ListCell align="right">
                        <FormattedNumber
                          maximumFractionDigits={2}
                          value={distribution.orderQuantity || 0}
                          style={`unit`}
                          unit="kilogram"
                        />
                      </ListCell>
                      <ListCell align="right">
                        <Box display={'flex'} justifyContent={'end'}>
                          {isEditDisabled ? (
                            <Tooltip title={intl.formatMessage({ id: `${PAGE_NAME_ID}.list.editDisabled` })} sx={{ display: 'flex' }}>
                              <Button size="small" disabled={isEditDisabled}>
                                <FormattedMessage id={`${PAGE_NAME_ID}.distribute`} values={{ type: '' }} />
                              </Button>
                            </Tooltip>
                          ) : (
                            <Dialog
                              ref={(el) => {
                                if (el) createDistributionRef.current[idx] = el;
                              }}
                              title={
                                intl.formatMessage({ id: `${PAGE_NAME_ID}.distribute` }) +
                                ` #${_.first(distribution.products)?.orderNumber || ''}`
                              }
                              maxWidth={'lg'}
                              button={
                                <Button
                                  size="small"
                                  onClick={() => {
                                    if (shouldDisableEdit(distribution.eta)) {
                                      createDistributionRef.current[idx].setOpen(false);
                                      window.location.reload();
                                      return;
                                    }
                                  }}>
                                  <FormattedMessage id={`${PAGE_NAME_ID}.distribute`} />
                                </Button>
                              }>
                              <CreateOrderDistribution distributionId={distribution.id} />
                            </Dialog>
                          )}
                        </Box>
                      </ListCell>
                    </ListRow>
                  );
                })}
              </List>
            </Box>
          </Box>

          <Box>
            <Typography>
              <FormattedMessage id={`${PAGE_NAME_ID}.distributed`} />
            </Typography>
            <Divider sx={{ my: 4 }} />
          </Box>
          <Box>
            <List
              options={[{ id: 'warning', label: '', align: 'center' }, ...options]}
              rows={distributed}
              isLoading={isLoading}
              sort={setDistributed}
              noResult={{
                show: !distributed.length,
                text: intl.formatMessage({ id: `general.noResults` }),
              }}>
              {distributed?.map((distribution, idx) => {
                const isEditDisabled = shouldDisableEdit(distribution.eta);

                return (
                  <ListRow
                    key={idx}
                    body={{
                      options: [
                        ...bodyOptions,
                        {
                          id: 'quantity',
                          autoWidth: true,
                          label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.body.distributedQuantity` }),
                          align: 'right',
                        },
                      ],
                      span: 8,
                      children: distribution?.distributions
                        .sort((a, b) => a.siloName.toLowerCase().localeCompare(b.siloName.toLowerCase()))
                        .map((line, idxx) => (
                          <BodyTableRow key={`${idx}_body_${idxx}`} sx={{ width: '100%' }}>
                            <ListCell>{line.orderNumber}</ListCell>
                            <ListCell>{line.itemName}</ListCell>
                            <ListCell>{line.siloName}</ListCell>
                            <ListCell align="right">
                              <FormattedNumber value={line.quantity} style={`unit`} unit={'kilogram'} />
                            </ListCell>
                          </BodyTableRow>
                        )),
                    }}>
                    <ListCell>
                      <Box display={'flex'} justifyContent={'center'} alignItems={'center'} gap={4}>
                        {distribution.status === OrderDistributionStatus.SILO_CHANGED && (
                          <Tooltip arrow title={intl.formatMessage({ id: `${PAGE_NAME_ID}.list.configHasChanged` })}>
                            <AlertTriangle color={theme.palette.warning.light} size={18} />
                          </Tooltip>
                        )}
                      </Box>
                    </ListCell>
                    <ListCell>{distribution.siteName}</ListCell>
                    <ListCell>
                      <FormattedDate value={distribution.requestedDeliveryDate} />
                    </ListCell>
                    <ListCell>{distribution.vesselName}</ListCell>
                    <ListCell>
                      <FormattedDate value={distribution.eta} /> <FormattedTime value={distribution.eta} />
                    </ListCell>
                    <ListCell align="right">
                      <FormattedNumber
                        maximumFractionDigits={2}
                        value={distribution.orderQuantity || 0}
                        style={`unit`}
                        unit="kilogram"
                      />
                    </ListCell>
                    <ListCell align="right">
                      <Box display={'flex'} justifyContent={'end'}>
                        {isEditDisabled ? (
                          <Tooltip title={intl.formatMessage({ id: `${PAGE_NAME_ID}.list.editDisabled` })} sx={{ display: 'flex' }}>
                            <Button
                              size="small"
                              disabled={isEditDisabled}>
                              <FormattedMessage id={`general.edit`} values={{ type: '' }} />
                            </Button>
                          </Tooltip>
                        ) : (
                          <Dialog
                            ref={(el) => {
                              if (el) editDistributionRef.current[idx] = el;
                            }}
                            title={
                              intl.formatMessage({ id: `${PAGE_NAME_ID}.distribute` }) +
                              ` #${_.first(distribution.products)?.orderNumber || ''}`
                            }
                            maxWidth={'lg'}
                            button={
                              <Button
                                size="small"
                                onClick={() => {
                                  if (shouldDisableEdit(distribution.eta)) {
                                    window.location.reload();
                                    return;
                                  }
                                }}>
                                <FormattedMessage id={`general.edit`} values={{ type: '' }} />
                              </Button>
                            }>
                            <CreateOrderDistribution distributionId={distribution.id} />
                          </Dialog>
                        )}
                      </Box>
                    </ListCell>
                  </ListRow>
                );
              })}
            </List>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

const ListOrderDistributionsContainer: React.FC = () => (
  <OrderFeedProvider>
    <ListOrderDistributions />
  </OrderFeedProvider>
);

export default React.memo(ListOrderDistributionsContainer);
